import { MenuItem, Box, Typography, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { useCollections } from '../hooks/useCollections';
import { en } from '../translations/en';

const PERMISSION_LABELS: Record<string, string> = {
  view: en.catalogSwitcher.permission.view,
  full: en.catalogSwitcher.permission.full,
};

interface Props {
  /**
   * Invoked after a row is selected (e.g. to close the parent Menu or
   * mobile drawer). The selection itself — switching active catalog and
   * navigating — is handled inside the list.
   */
  onSelect?: () => void;
}

/**
 * Renders one MenuItem per accessible catalog. Used by both the desktop
 * Catalog dropdown (anchored to the nav Tab) and the mobile drawer. Selection
 * routes through `switchToCatalogAliId` so the legacy `activeCatalogOwnerId`
 * stays in sync for SearchContext / CollectionsContext / FieldFavorites etc.
 *
 * Returns null when there are fewer than 2 accessible catalogs — single-
 * catalog users have nothing to switch between, so the menu collapses to a
 * plain Link rendered by the caller.
 */
export const CatalogMenuList = ({ onSelect }: Props) => {
  const navigate = useNavigate();
  const { availableCatalogs, switchToCatalogAliId } = useActiveCatalog();
  const { selectCollection } = useCollections();

  if (availableCatalogs.length <= 1) return null;

  const handleSelect = (aliId: string) => {
    switchToCatalogAliId(aliId);
    selectCollection(null);
    // Always navigate to My Objects, even from /albums (GH#1236/#1235). The
    // Albums page renders the cross-catalog union of every accessible album
    // (see pages/Collections.tsx), so it has no per-catalog view to preserve
    // by staying put — an earlier special case that stayed on /albums (#1224)
    // became a dead-looking no-op once that union view shipped, since
    // switching catalogs there produced no visible change at all.
    navigate('/my-objects');
    onSelect?.();
  };

  return (
    <>
      {availableCatalogs.map((row) => (
        <MenuItem
          key={row.catalogAliId}
          onClick={() => handleSelect(row.catalogAliId)}
        >
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <Typography>
              {row.label} ({row.catalogAliId})
            </Typography>
            <Chip
              label={
                row.kind === 'delegated'
                  ? en.catalogSwitcher.delegatedChip
                  : en.catalogSwitcher.ownerChip
              }
              size="small"
              color={row.kind === 'delegated' ? 'default' : 'primary'}
              variant="outlined"
              sx={{ ml: 'auto' }}
            />
            {row.kind === 'delegated' && row.delegation && (
              <Chip
                label={
                  PERMISSION_LABELS[row.delegation.permissions] ||
                  row.delegation.permissions
                }
                size="small"
              />
            )}
          </Box>
        </MenuItem>
      ))}
    </>
  );
};
